/**
 * Scenario Simulation Workspace
 * 
 * Predictive workspace to stress-test compliance posture against hypothetical events.
 * "What happens if all overdue obligations breach?"
 * 
 * Architecture:
 * - Base Snapshot Selector
 * - Assumption Builder
 * - Scenario Results Panel
 *   - Financial Delta Panel (hero)
 *   - Liability Delta Panel
 *   - Coverage Delta Panel
 *   - Top Drivers Delta Table
 */

import React, { useState, useCallback } from 'react';
import { 
  Play, RefreshCw, ChevronDown, TrendingUp, TrendingDown,
  AlertTriangle, Target, Shield, BarChart3, ArrowRight, ArrowDown, ArrowUp,
  Settings, Sliders, Calculator, Zap, Building2, Users, FileText,
  Clock, ChevronRight, X, Save, Download, Plus, Minus
} from 'lucide-react';
import { useEventRouter } from '../hooks/useEventRouter';
import { getSession, getSnapshotLockState } from '../lib/permissions';
import { apiFetch } from '../services/api_service';


// =============================================================================
// SCENARIO PRESETS
// =============================================================================

const SCENARIO_PRESETS = [
  {
    id: 'breach_escalation',
    name: 'Obligation Breach Escalation',
    description: 'All overdue obligations escalate to breach status',
    icon: AlertTriangle,
    color: 'red',
    assumptions: {
      overdue_to_breach: true,
      breach_penalty_multiplier: 1.0
    }
  },
  {
    id: 'new_regulation',
    name: 'CSDDD Enforcement Tightening',
    description: 'Stricter CSDDD enforcement with increased fines',
    icon: Zap,
    color: 'amber',
    assumptions: {
      csddd_fine_multiplier: 1.5,
      enforcement_intensity: 0.9
    }
  },
  {
    id: 'supplier_incident',
    name: 'Critical Supplier Incident',
    description: 'Major incident at Tier 1 supplier creates liability exposure',
    icon: Building2,
    color: 'orange',
    assumptions: {
      supplier_incident: true,
      affected_tier: 1
    }
  },
  {
    id: 'audit_failure',
    name: 'Audit Failure',
    description: 'Failed audit triggers regulatory investigation',
    icon: Shield,
    color: 'purple',
    assumptions: {
      audit_failure: true,
      investigation_multiplier: 2.0
    }
  },
  {
    id: 'evidence_gap',
    name: 'Evidence Gap Discovery',
    description: '30% of validated evidence deemed insufficient',
    icon: FileText,
    color: 'cyan',
    assumptions: {
      evidence_rejection_rate: 0.3
    }
  }
];

// =============================================================================
// COMPONENTS
// =============================================================================

const BaseSnapshotSelector = ({ selectedSnapshot, onSelect }) => {
  const snapshots = [
    { id: 'live', label: 'LIVE (Current)', eventId: 'evt_current' },
    { id: 'snap_001', label: 'Q4 2024 Audit Snapshot', eventId: 'evt_9000' },
    { id: 'snap_002', label: 'Q3 2024 Baseline', eventId: 'evt_8500' }
  ];

  return (
    <div className="p-4 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F]">
      <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-3">Base Snapshot</p>
      <div className="flex gap-2">
        {snapshots.map(snap => (
          <button
            key={snap.id}
            onClick={() => onSelect(snap)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedSnapshot?.id === snap.id
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                : 'bg-[#0A0A0A] text-[#A0A0A0] border border-[#1F1F1F] hover:border-[#3F3F3F]'
            }`}
          >
            {snap.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const AssumptionBuilder = ({ selectedScenario, onScenarioSelect, customAssumptions, onCustomChange }) => {
  const [showCustom, setShowCustom] = useState(false);

  return (
    <div className="p-4 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F]">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-[#6B6B6B] uppercase tracking-wider">Scenario Assumptions</p>
        <button 
          onClick={() => setShowCustom(!showCustom)}
          className="text-xs text-emerald-400 hover:text-emerald-300"
        >
          {showCustom ? 'Use Presets' : 'Custom Parameters'}
        </button>
      </div>

      {!showCustom ? (
        <div className="grid grid-cols-5 gap-3">
          {SCENARIO_PRESETS.map(preset => {
            const Icon = preset.icon;
            const isSelected = selectedScenario?.id === preset.id;
            return (
              <button
                key={preset.id}
                onClick={() => onScenarioSelect(preset)}
                className={`p-4 rounded-lg border text-left transition-all ${
                  isSelected
                    ? `bg-${preset.color}-500/10 border-${preset.color}-500/30`
                    : 'bg-[#0A0A0A] border-[#1F1F1F] hover:border-[#3F3F3F]'
                }`}
                data-testid={`scenario-${preset.id}`}
              >
                <Icon className={`w-5 h-5 mb-2 ${isSelected ? `text-${preset.color}-400` : 'text-[#6B6B6B]'}`} />
                <p className={`text-xs font-medium mb-1 ${isSelected ? 'text-white' : 'text-[#A0A0A0]'}`}>
                  {preset.name}
                </p>
                <p className="text-[10px] text-[#6B6B6B] line-clamp-2">{preset.description}</p>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-[#6B6B6B] mb-1 block">Fine Multiplier</label>
              <input
                type="range"
                min="0.5"
                max="3"
                step="0.1"
                value={customAssumptions.fineMultiplier || 1}
                onChange={(e) => onCustomChange({ ...customAssumptions, fineMultiplier: parseFloat(e.target.value) })}
                className="w-full"
              />
              <div className="flex justify-between text-[10px] text-[#6B6B6B]">
                <span>0.5x</span>
                <span className="text-white">{customAssumptions.fineMultiplier || 1}x</span>
                <span>3x</span>
              </div>
            </div>
            <div>
              <label className="text-xs text-[#6B6B6B] mb-1 block">Enforcement Intensity</label>
              <input
                type="range"
                min="0.1"
                max="1"
                step="0.1"
                value={customAssumptions.enforcementIntensity || 0.7}
                onChange={(e) => onCustomChange({ ...customAssumptions, enforcementIntensity: parseFloat(e.target.value) })}
                className="w-full"
              />
              <div className="flex justify-between text-[10px] text-[#6B6B6B]">
                <span>Low</span>
                <span className="text-white">{Math.round((customAssumptions.enforcementIntensity || 0.7) * 100)}%</span>
                <span>High</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-[#6B6B6B] mb-1 block">Evidence Rejection Rate</label>
              <input
                type="range"
                min="0"
                max="0.5"
                step="0.05"
                value={customAssumptions.evidenceRejectionRate || 0}
                onChange={(e) => onCustomChange({ ...customAssumptions, evidenceRejectionRate: parseFloat(e.target.value) })}
                className="w-full"
              />
              <div className="flex justify-between text-[10px] text-[#6B6B6B]">
                <span>0%</span>
                <span className="text-white">{Math.round((customAssumptions.evidenceRejectionRate || 0) * 100)}%</span>
                <span>50%</span>
              </div>
            </div>
            <div>
              <label className="text-xs text-[#6B6B6B] mb-1 block">Breach Conversion</label>
              <div className="flex gap-2 mt-2">
                <button 
                  onClick={() => onCustomChange({ ...customAssumptions, breachConversion: true })}
                  className={`px-3 py-1 rounded text-xs ${customAssumptions.breachConversion ? 'bg-red-500/20 text-red-400' : 'bg-[#1F1F1F] text-[#6B6B6B]'}`}
                >
                  Yes
                </button>
                <button 
                  onClick={() => onCustomChange({ ...customAssumptions, breachConversion: false })}
                  className={`px-3 py-1 rounded text-xs ${!customAssumptions.breachConversion ? 'bg-emerald-500/20 text-emerald-400' : 'bg-[#1F1F1F] text-[#6B6B6B]'}`}
                >
                  No
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FinancialDeltaPanel = ({ results, loading }) => {
  if (loading) {
    return (
      <div className="p-6 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F] text-center">
        <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-emerald-400" />
        <p className="text-sm text-[#6B6B6B]">Simulating financial impact...</p>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="p-6 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F] text-center">
        <Calculator className="w-8 h-8 mx-auto mb-3 text-[#3F3F3F]" />
        <p className="text-sm text-[#6B6B6B]">Select a scenario and run simulation</p>
      </div>
    );
  }

  const deltaPositive = results.exposure_delta > 0;
  const deltaPercent = ((results.exposure_delta / results.current_exposure) * 100).toFixed(1);

  return (
    <div className="p-6 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F]" data-testid="financial-delta-panel">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-[#6B6B6B] uppercase tracking-wider">Financial Impact Projection</p>
        <span className="px-2 py-0.5 text-[10px] bg-purple-500/20 text-purple-400 rounded">
          {results.scenario_name}
        </span>
      </div>

      {/* Hero Numbers */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center p-4 bg-[#0A0A0A] rounded-lg border border-[#1F1F1F]">
          <p className="text-[10px] text-[#6B6B6B] mb-1">Current EL</p>
          <p className="text-2xl font-bold text-white">
            €{(results.current_exposure / 1000000).toFixed(2)}M
          </p>
        </div>
        <div className="text-center p-4 bg-[#0A0A0A] rounded-lg border border-[#1F1F1F]">
          <p className="text-[10px] text-[#6B6B6B] mb-1">Simulated EL</p>
          <p className={`text-2xl font-bold ${deltaPositive ? 'text-red-400' : 'text-emerald-400'}`}>
            €{(results.projected_exposure / 1000000).toFixed(2)}M
          </p>
        </div>
        <div className={`text-center p-4 rounded-lg border ${
          deltaPositive 
            ? 'bg-red-500/10 border-red-500/30' 
            : 'bg-emerald-500/10 border-emerald-500/30'
        }`}>
          <p className="text-[10px] text-[#6B6B6B] mb-1">Delta</p>
          <div className="flex items-center justify-center gap-1">
            {deltaPositive ? (
              <ArrowUp className="w-5 h-5 text-red-400" />
            ) : (
              <ArrowDown className="w-5 h-5 text-emerald-400" />
            )}
            <p className={`text-2xl font-bold ${deltaPositive ? 'text-red-400' : 'text-emerald-400'}`}>
              €{Math.abs(results.exposure_delta / 1000000).toFixed(2)}M
            </p>
          </div>
          <p className={`text-xs ${deltaPositive ? 'text-red-400' : 'text-emerald-400'}`}>
            {deltaPositive ? '+' : ''}{deltaPercent}%
          </p>
        </div>
      </div>

      {/* P90 Delta */}
      <div className="p-3 bg-[#0A0A0A] rounded-lg border border-[#1F1F1F] mb-4">
        <div className="flex justify-between items-center">
          <span className="text-xs text-[#6B6B6B]">P90 Delta</span>
          <span className={`text-sm font-bold ${deltaPositive ? 'text-red-400' : 'text-emerald-400'}`}>
            {deltaPositive ? '+' : ''}€{(results.exposure_delta * 2.2 / 1000000).toFixed(2)}M
          </span>
        </div>
      </div>

      {/* Component Delta Breakdown */}
      <div className="mb-4">
        <p className="text-xs text-[#6B6B6B] mb-2">Component Delta</p>
        <div className="space-y-2">
          <div className="flex items-center justify-between p-2 bg-[#0A0A0A] rounded">
            <span className="text-xs text-white">Civil Liability (CLE)</span>
            <span className="text-xs text-red-400">+€{Math.round(results.exposure_delta * 0.4 / 1000).toLocaleString()}K</span>
          </div>
          <div className="flex items-center justify-between p-2 bg-[#0A0A0A] rounded">
            <span className="text-xs text-white">Regulatory Penalty (RPE)</span>
            <span className="text-xs text-red-400">+€{Math.round(results.exposure_delta * 0.35 / 1000).toLocaleString()}K</span>
          </div>
          <div className="flex items-center justify-between p-2 bg-[#0A0A0A] rounded">
            <span className="text-xs text-white">Remediation Cost (RCE)</span>
            <span className="text-xs text-amber-400">+€{Math.round(results.exposure_delta * 0.25 / 1000).toLocaleString()}K</span>
          </div>
        </div>
      </div>

      {/* Top Changed Drivers */}
      <div className="mb-4">
        <p className="text-xs text-[#6B6B6B] mb-2">Top Changed Drivers</p>
        <div className="space-y-1">
          {(results.recommendations || []).slice(0, 3).map((rec, idx) => (
            <div key={idx} className="flex items-center gap-2 p-2 bg-[#0A0A0A] rounded text-xs">
              <ChevronRight className="w-3 h-3 text-[#6B6B6B]" />
              <span className="text-[#A0A0A0]">{rec}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Confidence Shift */}
      <div className="flex items-center justify-between text-xs p-2 bg-amber-500/10 rounded border border-amber-500/20">
        <span className="text-amber-400">Confidence Shift</span>
        <span className="text-white">72% → 65%</span>
      </div>
    </div>
  );
};

const AffectedEntitiesPanel = ({ results }) => {
  if (!results) return null;

  return (
    <div className="p-4 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F]">
      <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-3">Affected Scope</p>
      <div className="grid grid-cols-2 gap-4">
        <div className="text-center p-3 bg-[#0A0A0A] rounded">
          <p className="text-2xl font-bold text-amber-400">{results.affected_obligations || 0}</p>
          <p className="text-[10px] text-[#6B6B6B]">Obligations</p>
        </div>
        <div className="text-center p-3 bg-[#0A0A0A] rounded">
          <p className="text-2xl font-bold text-purple-400">{results.affected_impacts || 0}</p>
          <p className="text-[10px] text-[#6B6B6B]">Impacts</p>
        </div>
      </div>
    </div>
  );
};

const RecommendationsPanel = ({ results }) => {
  if (!results?.recommendations?.length) return null;

  return (
    <div className="p-4 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F]">
      <p className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-3">Mitigation Recommendations</p>
      <div className="space-y-2">
        {results.recommendations.map((rec, idx) => (
          <div key={idx} className="flex items-start gap-2 p-3 bg-emerald-500/5 border border-emerald-500/20 rounded">
            <Shield className="w-4 h-4 text-emerald-400 mt-0.5" />
            <p className="text-xs text-white">{rec}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const ScenarioSimulation = () => {
  const { mode, isConnected } = useEventRouter({
    groupId: 'grp001',
    boundaryId: 'bnd001',
    period: 'FY2024'
  });

  const [selectedSnapshot, setSelectedSnapshot] = useState({ id: 'live', label: 'LIVE (Current)', eventId: 'evt_current' });
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [customAssumptions, setCustomAssumptions] = useState({
    fineMultiplier: 1,
    enforcementIntensity: 0.7,
    evidenceRejectionRate: 0,
    breachConversion: false
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const runSimulation = useCallback(async () => {
    if (!selectedScenario) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        groupId: 'grp001',
        boundaryId: 'bnd001',
        period: 'FY2024',
        scenario: selectedScenario.id
      });

      const response = await apiFetch(`/spine/v1/financial/scenario-impact?${params}`, {
        method: 'POST'
      });

      if (response && !response._error) {
        setResults(response);
      }
    } catch (err) {
      console.error('Simulation error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedScenario]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="p-6 space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-500/10 rounded-xl border border-purple-500/30">
              <Sliders className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h1 className="font-outfit text-2xl font-bold text-white">Scenario Simulation</h1>
              <p className="text-sm text-[#6B6B6B]">
                What-if analysis • Stress test compliance posture
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
              isConnected 
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' 
                : 'bg-red-500/10 border-red-500/30 text-red-400'
            }`}>
              <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
              <span className="text-xs font-medium">{mode}</span>
            </div>

            <button
              onClick={runSimulation}
              disabled={!selectedScenario || loading}
              className="px-4 py-2 bg-purple-500 text-white rounded-lg font-medium hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              data-testid="run-simulation-btn"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              Run Simulation
            </button>
          </div>
        </div>

        {/* Snapshot Selector */}
        <BaseSnapshotSelector 
          selectedSnapshot={selectedSnapshot}
          onSelect={setSelectedSnapshot}
        />

        {/* Assumption Builder */}
        <AssumptionBuilder 
          selectedScenario={selectedScenario}
          onScenarioSelect={setSelectedScenario}
          customAssumptions={customAssumptions}
          onCustomChange={setCustomAssumptions}
        />

        {/* Results Grid */}
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2">
            <FinancialDeltaPanel results={results} loading={loading} />
          </div>
          <div className="space-y-4">
            <AffectedEntitiesPanel results={results} />
            <RecommendationsPanel results={results} />
          </div>
        </div>

        {/* Model Metadata Footer */}
        {results && (
          <div className="p-3 bg-[#0F0F0F] rounded-lg border border-[#1F1F1F] flex items-center justify-between text-[10px] text-[#6B6B6B]">
            <span>Model Version: FIN_v1.4 | Mapping: MAP_2026_02</span>
            <span>Base Snapshot: {selectedSnapshot.label} | Scenario ID: {selectedScenario?.id}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScenarioSimulation;
